import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import {
  ATTRIBUTE_ENTITY,
  SYSTEM_ATTRIBUTE_PROPERTY,
} from "~/core/constants/attributes";
import { cn } from "~/core/generic/shadcn-utils";
import type {
  AttributeEntity,
  AttributeType,
  SystemAttributeProperty,
} from "~/core/types/common.type";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faLinkedinIn, faTwitter } from "@fortawesome/free-brands-svg-icons";
import {
  faBuilding,
  faCircleCaretDown,
  faCircleDot,
  faClock,
  faCoin,
  faEnvelope,
  faGlobe,
  faHashtag,
  faLink,
  faLoader,
  faLocationDot,
  faPhone,
  faRectangleHistory,
  faSquareCheck,
  faStar,
  faText,
  faUsers,
} from "@fortawesome/pro-regular-svg-icons";

type ColumnIconProps = {
  attributeType?: AttributeType;
  entityType?: AttributeEntity;
  field?: SystemAttributeProperty | string;
  customFaIcon?: IconProp;
  className?: string;
};

export default function ColumnIcon({
  attributeType,
  customFaIcon,
  entityType,
  field,
  className,
}: ColumnIconProps) {
  const iconData: IconProp = useMemo(() => {
    if (customFaIcon) return customFaIcon;

    if (entityType === ATTRIBUTE_ENTITY.TASK) {
      return faLoader;
    }

    switch (field) {
      case SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_NAME:
        return faBuilding;
      case SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_DOMAIN:
        return faGlobe;
      case SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_EMAIL:
        return faEnvelope;
      case SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_LINKEDIN:
      case SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_LINKEDIN:
        return faLinkedinIn;
      case SYSTEM_ATTRIBUTE_PROPERTY.PEOPLE_TWITTER:
      case SYSTEM_ATTRIBUTE_PROPERTY.COMPANY_TWITTER:
        return faTwitter;
      default:
        break;
    }

    switch (attributeType) {
      case "string":
        return faText;
      case "number":
        return faHashtag;
      case "checkbox":
        return faSquareCheck;
      case "date":
        return faClock;
      case "location":
        return faLocationDot;
      case "timestamp":
        return faClock;
      case "select":
        return faCircleCaretDown;
      case "multi_select":
        return faRectangleHistory;
      case "currency":
        return faCoin;
      case "rating":
        return faStar;
      case "email":
        return faEnvelope;
      case "phone":
        return faPhone;
      case "website":
        return faGlobe;
      case "radio":
        return faCircleDot;
      case "team_member":
        return faUsers;
      case "url":
        return faLink;
      default:
        return faText;
    }
  }, [attributeType, customFaIcon, field, entityType]);

  return (
    <FontAwesomeIcon
      icon={iconData}
      className={cn("size-3 text-light rounded-sm", className)}
    />
  );
}
